import { ReactNode, useMemo } from 'react';
import { Menu } from '@headlessui/react';
import { ChevronDownIcon as ArrowIcon } from '@heroicons/react/24/solid';
import { useCurrentRefinements, useHits } from 'react-instantsearch-hooks-web';
import { useFormat } from 'helpers/hooks/useFormat';
import { useLocalizedIndex } from 'providers/algolia/localized-index';
import useDynamicFacets from '../../hooks/useDynamicFacets';
import styles from '../../styles/index.module.css';
import { FacetConfiguration } from '../../types';
import SortFacet from '../facets/sort';
import AccordionBtn from 'components/commercetools-ui/atoms/accordion';
import { useProductList } from '../../context';
import { XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
import { useParams } from 'next/navigation';
import usePath from 'helpers/hooks/usePath';
import { localeMapping } from 'helpers/constants/localeMapping';

interface Props {
  facetsConfiguration: Record<string, FacetConfiguration>;
}
interface NoResultProps {
  fallback: ReactNode;
}
function NoResultsBoundary({ children, fallback }: React.PropsWithChildren<NoResultProps>) {
  const { results } = useHits();
  // The `__isArtificial` flag makes sure not to display the No Results message
  // when no hits have been returned.
  if (results && !results?.__isArtificial && results?.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }
  return children;
}

const DesktopFacets: React.FC<Props> = ({ facetsConfiguration }) => {
  const { locale } = useParams();
  const  Path  = usePath();

  const { indexName: productsIndex } = useLocalizedIndex({ type: 'products' });

  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });

  const { results } = useHits();
  const { items } = useCurrentRefinements();
  const localeKey = localeMapping[locale];
  const { removeAllRefinements } = useProductList();

  const isOnClarkPath = Path.path.includes('/clark'); 
  let isSanitarywareSelected = false;
  if (isOnClarkPath) {
    isSanitarywareSelected = items.some(
      (item) =>
        (item.attribute === `categories.${localeKey}.lvl0` &&
         item.refinements.some(
           (refinement) => 
             refinement.value === 'Sanitaryware' || 
             refinement.value === 'Tapware'
         )) ||
        (item.attribute === 'variants.searchableAttributes.colour' &&
         item.refinements.some(
           (refinement) => refinement.value === 'Anthracite Grey' ||
             refinement.value === 'Black'||
           refinement.value === 'Brushed Brass'
          )) ||
        (item.attribute === 'variants.searchableAttributes.item-material' &&
            item.refinements.some(
              (refinement) => refinement.value === 'Brass' 
          )) ||
          (item.attribute === 'searchableAttributes.range' &&
            item.refinements.some(
              (refinement) => refinement.value === 'Acqua' ||
                refinement.value === 'Aire' || 
                refinement.value === 'Aura' ||
                refinement.value === 'Urbane II' ||
                refinement.value === 'Urbane' ||
                refinement.value === 'Tribute' ||
                refinement.value === 'Trident' ||
                refinement.value === 'Uniset II' ||
                refinement.value === 'Universal' ||
                refinement.value === 'Track' ||
                refinement.value === 'Torres' 
        ))
    );

    Object.keys(facetsConfiguration).forEach((key) => {
      const facet = facetsConfiguration[key];
      if (key === 'variants.attributes.wels-with-flow-rate-flush-volume-1') {
        facet.label = isSanitarywareSelected ? 'WELS Rating' : 'WELS Rating (Shower)';
      }
    });
  }




  // Checking Facets "IsAS14281Compliant", "IsAS14281Ambulant" & "IsNZ41121Compliant"
  // In PLP page 
  // If locale is NZ then we will show IsNZ41121Compliant    and    if locale is AU then we will show "IsAS14281Compliant" & "IsAS14281Ambulant"

  // Filter out facets based on AU and NZ
  const filteredFacetsConfiguration = Object.fromEntries(
    Object.entries(facetsConfiguration).filter(([key]) => {
      if (locale === 'nz') {
        return key !== 'variants.attributes.IsAS14281Compliant' && key !== 'variants.attributes.IsAS14281Ambulant';
      }
      else if (locale === 'au') {
        return key !== 'variants.attributes.IsNZ41121Compliant';
      }
      return true;
    })
  );

  const facets = useDynamicFacets({
    configuration: filteredFacetsConfiguration,
    ordering: results?.renderingContent?.facetOrdering?.facets?.order,
    render: ({ attribute, Component }) => (
      <AccordionBtn
        closedSectionTitle={filteredFacetsConfiguration[attribute].label}
        className="min-w-[15rem] border-b border-[#808080] 2xl:min-w-[20rem]"
        buttonClassName="py-16 text-14 2xl:text-16 font-normal text-[#090909] text-left "
        panelClassName="text-14 2xl:text-16 text-[#090909] font-normal w-[15rem] 2xl:min-w-[20rem]"
        iconClassName="w-18 text-primary-black"
      >
        <span>{Component}</span>
      </AccordionBtn>
    ),
  });
  
  return (
    <div className="hidden items-center justify-between pb-16  lg:flex">
      <div className="flex flex-col lg:min-w-[15rem] 2xl:min-w-[20rem]">
        <div className="flex items-center justify-between border-b border-[#808080] pb-24 text-28 font-medium text-[#333] 2xl:text-32">
          {formatProductMessage({ id: 'filters', defaultMessage: 'Filters' })}
          <NoResultsBoundary fallback={<span></span>}>
            <span
              className="text-12 text-[#333] underline hover:cursor-pointer 2xl:text-16"
              onClick={removeAllRefinements}
            >
              {formatProductMessage({ id: 'clear.all', defaultMessage: 'Clear All' })}
            </span>
          </NoResultsBoundary>
        </div>
        {facets}
      </div>
    </div>
  );
};
export default DesktopFacets;